import { useEffect, useState } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { IoCheckmark } from 'react-icons/io5';
import { AuthMethodType } from '@lit-protocol/constants';
import { toast } from 'sonner';
import { AuthMethod } from '@lit-protocol/types';
import { useForm, Controller } from 'react-hook-form';
import { AsYouType, isValidPhoneNumber } from 'libphonenumber-js';
import { useLocalStorage } from 'usehooks-ts';
import { Form, FormControl, FormField, FormItem, FormMessage } from '../ui/form';
import { SelectItem, Select, SelectContent, SelectTrigger, SelectValue } from '../ui/select';
import classNames from '@/lib/misc/classNames';
import Loading from '../shared/Loading';
import { Button } from '../ui/button';
import { getAuthId } from '@/lib/auth/getAuthId';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Input } from '@/components/ui/input';
import { useSession } from '@/contexts/LitSessionProvider';
const OTPValidationSchema = z.object({
  code: z.string().min(6, {
    message: 'Please enter a valid 6 digit code.'
  })
});
const EmailValidationSchema = z.object({
  provider: z.string().email({
    message: 'Invalid email.'
  })
});
const PhoneValidationSchema = z.object({
  countryCode: z.string(),
  phoneNumber: z.string()
}).refine(data => {
  const [country, code] = data.countryCode.split(':');
  const fullNumber = `+${code}${data.phoneNumber}`;
  return isValidPhoneNumber(fullNumber, country);
}, {
  message: 'Please enter a valid phone number.',
  path: ['phoneNumber']
});
export const StytchFactorOtp = ({
  authMethodType,
  askingAuthId,
  askingProviderValue,
  callBack,
  disableInput = false,
  pastAuthMethod,
  closeDialog,
  title,
  skipFinalConfirmationScreen = false
}: {
  authMethodType: number;
  disableInput?: boolean;
  askingProviderValue?: string;
  callBack: (authMethod: AuthMethod, providerValue: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
  pastAuthMethod?: string;
  closeDialog?: () => void;
  title?: string;
  skipFinalConfirmationScreen?: boolean;
  askingAuthId?: string;
}) => {
  const [providerValue, setProviderValue] = useState<string | null>(null);
  const [stytchId, setStytchId] = useLocalStorage('otp-stytchId', null);
  const [authMethod, setAuthMethod] = useState<string | null>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otpValidated, setOtpValidated] = useState(false);
  const [step, setStep] = useState<number>(1);
  const {
    clearLitSession
  } = useSession();
  const formCode = useForm<z.infer<typeof OTPValidationSchema>>({
    resolver: zodResolver(OTPValidationSchema),
    defaultValues: {
      code: ''
    }
  });
  const formEmail = useForm<z.infer<typeof EmailValidationSchema>>({
    resolver: zodResolver(EmailValidationSchema),
    defaultValues: {
      provider: askingProviderValue ? askingProviderValue : ''
    }
  });
  const formPhone = useForm<z.infer<typeof PhoneValidationSchema>>({
    resolver: zodResolver(PhoneValidationSchema),
    defaultValues: {
      countryCode: 'US:1',
      phoneNumber: askingProviderValue ? askingProviderValue : ''
    }
  });
  const form = authMethodType === AuthMethodType.StytchEmailFactorOtp ? formEmail : formPhone;
  useEffect(() => {
    setAuthMethod(pastAuthMethod);
  }, [pastAuthMethod]);
  const providerName = () => {
    switch (authMethodType) {
      case AuthMethodType.StytchEmailFactorOtp:
        return 'email';
      case AuthMethodType.StytchSmsFactorOtp:
        return 'text message/sms';
      case AuthMethodType.StytchWhatsAppFactorOtp:
        return 'WhatsApp';
      default:
        return '';
    }
  };
  const GetOTP = async (values: any, override?: string) => {
    const provider = authMethodType === AuthMethodType.StytchEmailFactorOtp ? 'StytchEmailFactorOtp' : authMethodType === AuthMethodType.StytchSmsFactorOtp ? 'StytchSmsFactorOtp' : 'StytchWhatsAppFactorOtp';

    //if override is syntheticbaseevent, then use the provider value from the form

    if (typeof override != 'string') {
      override = null;
    }
    const providerValue = override ? override : authMethodType === AuthMethodType.StytchEmailFactorOtp ? values.provider : `+${values.countryCode.split(':')[1]}${values.phoneNumber}`.replaceAll('(', '').replaceAll(')', '').replaceAll('-', '').replaceAll(' ', '');
    setLoading(true);
    try {
      const response = await fetch('/api/auth/get-stytch-otp', {
        method: 'POST',
        body: JSON.stringify({
          provider,
          providerValue
        })
      });
      const json = await response.json();
      console.log('get-stytch-otp', json);
      if (json.status === 'success') {
        setStytchId(json['stytch_id']);
        setProviderValue(providerValue);
        setError(null);
        toast.success('Verification code sent', {
          duration: 1000
        });
        setStep(2);
      } else {
        setError(json.error);
      }
    } catch (err) {
      setError(err.message);
      //reset form
      form.reset();
    }
    setLoading(false);
  };
  const ValidateOTP = async (values: z.infer<typeof OTPValidationSchema>) => {
    setLoading(true);
    try {
      const response = await fetch('/api/auth/validate-stytch-otp', {
        method: 'POST',
        body: JSON.stringify({
          otp: values.code,
          stytch_id: stytchId
        })
      });
      const json = await response.json();
      console.log('validate-stytch-otp values', json);
      if (json.status === 'success') {
        const _authMethod = {
          authMethodType: authMethodType,
          accessToken: json['session_jwt']
        };
        if (askingAuthId) {
          const _authId = await getAuthId(_authMethod);
          if (askingAuthId != _authId) {
            throw new Error(`Invalid ${authMethodType == AuthMethodType.StytchEmailFactorOtp ? 'Email' : 'Mobile number'} provided for authentication`);
          }
        }
        const result = await callBack(_authMethod, providerValue);
        if (!result.success) {
          setError(result.error);
        } else {
          setOtpValidated(true);
          // toast(`Validation success.`);
          setStep(3);
        }
      } else {
        console.log('err values', json.error);
        setError(json.error);
      }
    } catch (err) {
      console.log('err values', err);
      setError(err.message);
    }
    setLoading(false);
  };
  const disconnectAuth = async () => {
    setLoading(true);
    if (!authMethod) {
      setError('No additional auth method to remove');
      setLoading(false);
      return;
    }
    setAuthMethod(null);
    const result = await callBack(null, null);
    if (!result.success) {
      setError(result.error);
    } else {
      toast(`Successfully removed ${providerName()} as a secondary login method`, {
        duration: 1000
      });
      closeDialog && closeDialog();
    }
    setLoading(false);
  };
  const [hasSent, setHasSent] = useLocalStorage('hasSentOTP', false);
  useEffect(() => {
    async function skipAndSend() {
      console.log('skipping and sending', global.hasSent);
      //set loading
      setLoading(true);
      await GetOTP({}, askingProviderValue);
      setLoading(false);
    }
    if (askingProviderValue) {
      if (!global.hasSent && !hasSent) {
        setHasSent(true);
        global.hasSent = true;
        skipAndSend();
      } else {
        setStep(2);
      }
    }
  }, [askingProviderValue]);
  return <>
      {loading ? <div className='w-full px-5 py-4 flex items-center text-genius-pink'>
          <Loading />
        </div> : <>
          {step === 1 && authMethod !== 'Synced' && !askingProviderValue && <div className='w-full'>
              <div className='flex flex-col gap-y-[2px] ta-l w-full px-6 mb-4'>
                <p className='text-md text-genius-cream ta-l w-full'>
                  {title && `Add ${title} Two Factor Authentication`}
                </p>
                {title && title !== 'Email' && <p className='text-xs text-genius-cream/50 mt-2'>
                    Please make sure to add your area code to ensure we can deliver messages
                  </p>}
              </div>
              <div className='px-6'>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(GetOTP)} className='w-full max-auto space-y-6'>
                    {authMethodType === AuthMethodType.StytchEmailFactorOtp ? <FormField control={form.control} name='provider' render={({
                field
              }) => <FormItem>
                            <Input className='rounded-md border-none bg-genius-blue text-genius-cream' disabled={loading || disableInput} {...field} value={disableInput ? `${new Array(field.value.split('@')[0].length).fill('*').join('')}` + field.value.split('@')[1] : field.value} placeholder='Enter your email address' />
                            <FormMessage className='py-2 ta-c bg-[#5E3046] text-[#F87171] rounded-md' />
                          </FormItem>} /> : <div className='flex items-center gap-2'>
                        <div className={classNames('w-[30%]', askingProviderValue ? 'w-0' : '')}>
                          {!askingProviderValue && <Controller name='countryCode' control={form.control} render={({
                    field
                  }) => <Select onValueChange={field.onChange} defaultValue={field.value}>
                                  <SelectTrigger className='bg-genius-blue text-genius-cream'>
                                    <SelectValue placeholder='Select country code' />
                                  </SelectTrigger>
                                  <SelectContent className='bg-genius-blue text-genius-cream' style={{
                      zIndex: 9999
                    }}>
                                    {Object.entries(countryCodes).map(([country, code]) => <SelectItem key={country} value={`${country}:${code}`}>
                                        {country} (+{code})
                                      </SelectItem>)}
                                  </SelectContent>
                                </Select>} />}
                        </div>
                        <div className={classNames('w-[70%]', askingProviderValue ? 'w-full' : '')}>
                          <Controller name='phoneNumber' control={form.control} render={({
                    field
                  }) => {
                    return <FormItem>
                                  <Input className='rounded-md border-none bg-genius-blue text-genius-cream' disabled={loading || disableInput} {...field} value={field.value} onChange={e => {
                        const {
                          countryCode
                        } = form.getValues();
                        const country = countryCode.split(':')[0];
                        const prevValue = field.value;
                        const newValue = e.target.value;
                        const isBackspace = prevValue.length > newValue.length;
                        if (isBackspace) {
                          field.onChange(newValue);
                        } else {
                          const formatted = new AsYouType(country).input(e.target.value);
                          field.onChange(formatted);
                        }
                      }} placeholder='Enter your phone number' />
                                  <FormMessage className='py-2 my-5 ta-c bg-[#5E3046] text-[#F87171] rounded-md' />
                                </FormItem>;
                  }} />
                        </div>
                      </div>}
                    <Button disabled={loading} variant='pink' onClick={() => {
                formCode.reset();
              }} className='mt-4 w-full h-14 rounded-full' type='submit'>
                      {stytchId ? 'Resend ' : 'Send '} Verification Code
                    </Button>
                  </form>
                </Form>
              </div>
            </div>}

          {step === 1 && authMethod === 'Synced' && !askingProviderValue && <div className='w-full'>
              <Button variant='pink' className='mt-4 h-14 w-full rounded-full' onClick={disconnectAuth}>
                Disconnect
              </Button>
            </div>}

          {step === 2 && !otpValidated && <div className='w-full px-5 py-1'>
              <div className='flex flex-col gap-y-[2px] ta-c w-full'>
                <p className='text-md text-genius-cream ta-l w-full mb-4'>
                  Enter Verification Code
                </p>
              </div>
              <Form {...formCode}>
                <form onSubmit={formCode.handleSubmit(ValidateOTP)} className='w-full max-auto space-y-6'>
                  <FormField control={formCode.control} name='code' render={({
              field
            }) => <FormItem className='OPTInputs'>
                        <FormControl>
                          <InputOTP maxLength={6} {...field}>
                            <InputOTPGroup>
                              <InputOTPSlot index={0} />
                              <InputOTPSlot index={1} />
                              <InputOTPSlot index={2} />
                              <InputOTPSlot index={3} />
                              <InputOTPSlot index={4} />
                              <InputOTPSlot index={5} />
                            </InputOTPGroup>
                          </InputOTP>
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                  <div className='flex justify-center gap-4'>
                    <p onClick={async () => {
                if (askingProviderValue) {
                  GetOTP(form.getValues(), askingProviderValue);
                } else {
                  formCode.reset();
                  GetOTP(form.getValues(), null);
                }
              }} className='text-genius-cream/50 underline text-[16px] my-4  ta-c cursor-pointer'>
                      Resend
                    </p>
                    <p onClick={async () => {
                clearLitSession();
                window.location.href = '/home';
              }} className='text-genius-cream/50 underline text-[16px] my-4  ta-c cursor-pointer'>
                      Log Out
                    </p>
                    {/* {error && (
                      <p
                        onClick={() => {
                          router.reload();
                        }}
                        className='text-genius-cream/50 underline text-[16px] my-4  ta-c '
                      >
                        Reset
                      </p>
                     )} */}
                  </div>

                  <Button disabled={loading} variant='pink' className='mt-4 w-full h-14 rounded-full' type='submit'>
                    Verify Code
                  </Button>
                </form>
              </Form>
            </div>}

          {step === 3 && otpValidated && !skipFinalConfirmationScreen && <div className='w-full'>
              <IoCheckmark className='h-[32px] w-[32px] text-green-400 m-auto mb-4' />
              <div className='px-5'>
                <div className='text-genius-cream/50 leading-4'>
                  Your {authMethodType === AuthMethodType.StytchEmailFactorOtp ? 'email' : 'phone'}{' '}
                  has been added as a login method! The next time you log in, you'll be prompted to
                  enter a verification code sent to:
                  <br /> <br />
                  <span className='text-genius-cream mt-2'>{providerValue}</span>.
                </div>
              </div>
              <Button variant='pink' className='mt-4 h-14 w-full rounded-full' onClick={() => {
          setStep(1);
          closeDialog();
        }}>
                Done
              </Button>
            </div>}

          {error && <div className='w-full text-sm px-5 py-4 text-red-400 capitalize flex justify-center'>
              Error: {error?.includes('_') ? error.replaceAll('_', ' ') : error}
            </div>}
        </>}
    </>;
};
//TODO fix countries with multipe codes
const countryCodes = {
  US: '1',
  BD: '880',
  BE: '32',
  BF: '226',
  BG: '359',
  BA: '387',
  BB: '+1-246',
  WF: '681',
  BL: '590',
  BM: '+1-441',
  BN: '673',
  BO: '591',
  BH: '973',
  BI: '257',
  BJ: '229',
  BT: '975',
  JM: '+1-876',
  BV: '',
  BW: '267',
  WS: '685',
  BQ: '599',
  BR: '55',
  BS: '+1-242',
  JE: '+44-1534',
  BY: '375',
  BZ: '501',
  RU: '7',
  RW: '250',
  RS: '381',
  TL: '670',
  RE: '262',
  TM: '993',
  TJ: '992',
  RO: '40',
  TK: '690',
  GW: '245',
  GU: '+1-671',
  GT: '502',
  GS: '',
  GR: '30',
  GQ: '240',
  GP: '590',
  JP: '81',
  GY: '592',
  GG: '+44-1481',
  GF: '594',
  GE: '995',
  GD: '+1-473',
  GB: '44',
  GA: '241',
  SV: '503',
  GN: '224',
  GM: '220',
  GL: '299',
  GI: '350',
  GH: '233',
  OM: '968',
  TN: '216',
  JO: '962',
  HR: '385',
  HT: '509',
  HU: '36',
  HK: '852',
  HN: '504',
  HM: '',
  VE: '58',
  PR: '+1-787 and 1-939',
  PS: '970',
  PW: '680',
  PT: '351',
  SJ: '47',
  PY: '595',
  IQ: '964',
  PA: '507',
  PF: '689',
  PG: '675',
  PE: '51',
  PK: '92',
  PH: '63',
  PN: '870',
  PL: '48',
  PM: '508',
  ZM: '260',
  EH: '212',
  EE: '372',
  EG: '20',
  ZA: '27',
  EC: '593',
  IT: '39',
  VN: '84',
  SB: '677',
  ET: '251',
  SO: '252',
  ZW: '263',
  SA: '966',
  ES: '34',
  ER: '291',
  ME: '382',
  MD: '373',
  MG: '261',
  MF: '590',
  MA: '212',
  MC: '377',
  UZ: '998',
  MM: '95',
  ML: '223',
  MO: '853',
  MN: '976',
  MH: '692',
  MK: '389',
  MU: '230',
  MT: '356',
  MW: '265',
  MV: '960',
  MQ: '596',
  MP: '+1-670',
  MS: '+1-664',
  MR: '222',
  IM: '+44-1624',
  UG: '256',
  TZ: '255',
  MY: '60',
  MX: '52',
  IL: '972',
  FR: '33',
  IO: '246',
  SH: '290',
  FI: '358',
  FJ: '679',
  FK: '500',
  FM: '691',
  FO: '298',
  NI: '505',
  NL: '31',
  NO: '47',
  NA: '264',
  VU: '678',
  NC: '687',
  NE: '227',
  NF: '672',
  NG: '234',
  NZ: '64',
  NP: '977',
  NR: '674',
  NU: '683',
  CK: '682',
  XK: '',
  CI: '225',
  CH: '41',
  CO: '57',
  CN: '86',
  CM: '237',
  CL: '56',
  CC: '61',
  CA: '1',
  CG: '242',
  CF: '236',
  CD: '243',
  CZ: '420',
  CY: '357',
  CX: '61',
  CR: '506',
  CW: '599',
  CV: '238',
  CU: '53',
  SZ: '268',
  SY: '963',
  SX: '599',
  KG: '996',
  KE: '254',
  SS: '211',
  SR: '597',
  KI: '686',
  KH: '855',
  KN: '+1-869',
  KM: '269',
  ST: '239',
  SK: '421',
  KR: '82',
  SI: '386',
  KP: '850',
  KW: '965',
  SN: '221',
  SM: '378',
  SL: '232',
  SC: '248',
  KZ: '7',
  KY: '+1-345',
  SG: '65',
  SE: '46',
  SD: '249',
  DO: '+1-809 and 1-829',
  DM: '+1-767',
  DJ: '253',
  DK: '45',
  VG: '+1-284',
  DE: '49',
  YE: '967',
  DZ: '213',
  UY: '598',
  YT: '262',
  UM: '1',
  LB: '961',
  LC: '+1-758',
  LA: '856',
  TV: '688',
  TW: '886',
  TT: '+1-868',
  TR: '90',
  LK: '94',
  LI: '423',
  LV: '371',
  TO: '676',
  LT: '370',
  LU: '352',
  LR: '231',
  LS: '266',
  TH: '66',
  TF: '',
  TG: '228',
  TD: '235',
  TC: '+1-649',
  LY: '218',
  VA: '379',
  VC: '+1-784',
  AE: '971',
  AD: '376',
  AG: '+1-268',
  AF: '93',
  AI: '+1-264',
  VI: '+1-340',
  IS: '354',
  IR: '98',
  AM: '374',
  AL: '355',
  AO: '244',
  AQ: '',
  AS: '+1-684',
  AR: '54',
  AU: '61',
  AT: '43',
  AW: '297',
  IN: '91',
  AX: '+358-18',
  AZ: '994',
  IE: '353',
  ID: '62',
  UA: '380',
  QA: '974',
  MZ: '258'
};